import React, { useContext, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import CourseSearchInput from '../components/CourseSearchInput';
import { ArrowLeft, Calendar, Clock, MapPin, Users, DollarSign, Save, Loader2, Info, ImagePlus, X, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function CreateEvent() {
  const { user, loading, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { eventId } = useParams();
  const location = useLocation();
  const isEditMode = !!eventId;
  const [submitting, setSubmitting] = useState(false);
  const [flyerPreview, setFlyerPreview] = useState(null);
  const [flyerFile, setFlyerFile] = useState(null);
  const [uploadingFlyer, setUploadingFlyer] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(!isEditMode);
  const flyerInputRef = React.useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'casual_round',
    date: '',
    time: '',
    location: '',
    max_participants: '',
    entry_fee: '',
    flyer_image: '',
    external_signup_url: '',
    club_name: '',
    is_fundraiser: false,
    charity_info: '',
    sponsor_info: '',
    seeking_sponsors: false,
    expected_attendance: '',
    sponsorship_levels: [],
    sponsor_contact_email: ''
  });

  const isFundraiserUser = user?.role === 'fundraiser';

  React.useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
    if (!loading && user && !user.is_member) {
      toast.error('Active membership required to create events');
      navigate('/membership');
    }
  }, [loading, user, navigate]);

  // Load existing event data in edit mode
  React.useEffect(() => {
    if (isEditMode && user) {
      const fetchEvent = async () => {
        try {
          const res = await fetch(`${API}/events/${eventId}`, { credentials: 'include' });
          if (res.ok) {
            const event = await res.json();
            setFormData({
              title: event.title || '',
              description: event.description || '',
              event_type: event.event_type || 'casual_round',
              date: event.date || '',
              time: event.time || '',
              location: event.location || '',
              max_participants: event.max_participants ? String(event.max_participants) : '',
              entry_fee: event.entry_fee ? String(event.entry_fee) : '',
              flyer_image: event.flyer_image || '',
              external_signup_url: event.external_signup_url || '',
              club_name: event.club_name || '',
              is_fundraiser: !!event.is_fundraiser,
              charity_info: event.charity_info || '',
              sponsor_info: event.sponsor_info || '',
              seeking_sponsors: !!event.seeking_sponsors,
              expected_attendance: event.expected_attendance ? String(event.expected_attendance) : '',
              sponsorship_levels: event.sponsorship_levels || [],
              sponsor_contact_email: event.sponsor_contact_email || ''
            });
            if (event.flyer_image) {
              setFlyerPreview(`${window.location.origin}${event.flyer_image}`);
            }
          } else {
            toast.error('Event not found');
            navigate('/events');
          }
        } catch {
          toast.error('Failed to load event');
          navigate('/events');
        } finally {
          setDataLoaded(true);
        }
      };
      fetchEvent();
    }
  }, [isEditMode, eventId, user]);

  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFlyerSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = await compressImage(file);
    setFlyerFile(compressed);
    setFlyerPreview(URL.createObjectURL(compressed));
  };

  const removeFlyerImage = () => {
    setFlyerFile(null);
    setFlyerPreview(null);
    setFormData({ ...formData, flyer_image: '' });
    if (flyerInputRef.current) flyerInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.date || !formData.time || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      let flyerUrl = formData.flyer_image;

      // Upload flyer if selected
      if (flyerFile) {
        setUploadingFlyer(true);
        const flyerFormData = new FormData();
        flyerFormData.append('flyer', flyerFile);
        const flyerRes = await fetch(`${API}/events/upload-flyer`, {
          method: 'POST',
          credentials: 'include',
          body: flyerFormData,
        });
        const flyerData = await flyerRes.json();
        if (flyerRes.ok) {
          flyerUrl = flyerData.url;
        } else {
          toast.error(flyerData.detail || 'Failed to upload flyer');
          setSubmitting(false);
          setUploadingFlyer(false);
          return;
        }
        setUploadingFlyer(false);
      }

      const payload = {
          title: formData.title,
          description: formData.description,
          event_type: formData.event_type,
          date: formData.date,
          time: formData.time,
          location: formData.location,
          max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
          entry_fee: formData.entry_fee ? parseFloat(formData.entry_fee) : null,
          flyer_image: flyerUrl || null,
          external_signup_url: formData.external_signup_url ? formData.external_signup_url.trim() : null,
          club_name: formData.club_name ? formData.club_name.trim() : null,
          is_fundraiser: !!formData.is_fundraiser,
          charity_info: formData.charity_info ? formData.charity_info.trim() : null,
          sponsor_info: formData.sponsor_info ? formData.sponsor_info.trim() : null,
          seeking_sponsors: !!formData.seeking_sponsors,
          expected_attendance: formData.expected_attendance ? parseInt(formData.expected_attendance) : null,
          sponsorship_levels: (formData.sponsorship_levels || []).filter(l => l.name && (l.cost !== '' && l.cost != null)).map(l => ({ name: l.name.trim(), cost: parseFloat(l.cost), description: (l.description || '').trim() })),
          sponsor_contact_email: formData.sponsor_contact_email ? formData.sponsor_contact_email.trim() : null
      };

      const url = isEditMode ? `${API}/events/${eventId}` : `${API}/events`;
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (response.ok) {
        if (isEditMode) {
          toast.success('Event updated!');
        } else if (data.status === 'approved') {
          toast.success('Event created and published!');
        } else {
          toast.success('Event submitted for admin approval. You\'ll be notified once it\'s approved.');
        }
        navigate('/events');
      } else {
        toast.error(data.detail || `Failed to ${isEditMode ? 'update' : 'create'} event`);
      }
    } catch (error) {
      toast.error('An error occurred');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user || !dataLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav md:pb-8">
      <Navigation user={user} onLogout={logout} />

      <div className="max-w-2xl mx-auto px-4 md:px-8 pt-20 md:pt-24">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/events')}
          className="mb-4 text-bundle-green hover:bg-bundle-cream-dark -ml-2"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Events
        </Button>

        <div className="mb-6">
          <h1 className="font-heading font-bold text-3xl text-bundle-green mb-2">{isEditMode ? 'Edit Event' : 'Create Event'}</h1>
          <p className="font-body text-gray-600">Organize a golf outing for fellow members</p>
        </div>

        {/* Info Banner */}
        {!user.is_admin && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-body text-sm text-blue-800">
                <strong>Note:</strong> Your event will be reviewed by an admin before it's published. 
                You'll receive a notification once it's approved.
              </p>
            </div>
          </div>
        )}

        <Card className="bg-white border-bundle-cream-dark">
          <CardContent className="p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Event Title */}
              <div className="space-y-2">
                <Label className="font-body text-bundle-green">Event Title *</Label>
                <Input
                  data-testid="event-title-input"
                  placeholder="e.g., Saturday Morning Scramble"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                />
              </div>

              {/* Club Name */}
              <div className="space-y-2">
                <Label className="font-body text-bundle-green">Club Name</Label>
                <Input
                  data-testid="event-club-input"
                  placeholder="e.g., Breton Bay Golf & Country Club"
                  value={formData.club_name}
                  onChange={(e) => setFormData({ ...formData, club_name: e.target.value })}
                  className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                />
                <p className="text-xs text-gray-500">
                  Shown as <em>Title / Club Name</em> in the events feed.
                </p>
              </div>

              {/* Event Type */}
              <div className="space-y-2">
                <Label className="font-body text-bundle-green">Event Type *</Label>
                <Select
                  value={formData.event_type}
                  onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                >
                  <SelectTrigger data-testid="event-type-select" className="border-bundle-cream-dark">
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="casual_round">Casual Round</SelectItem>
                    <SelectItem value="weekly_game">Weekly Game</SelectItem>
                    <SelectItem value="tournament">Tournament</SelectItem>
                    <SelectItem value="social">Social Event</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-body text-bundle-green flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Date *
                  </Label>
                  <Input
                    type="date"
                    data-testid="event-date-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-bundle-green flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Time *
                  </Label>
                  <Input
                    type="time"
                    data-testid="event-time-input"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                  />
                </div>
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label className="font-body text-bundle-green flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Location / Course *
                </Label>
                <CourseSearchInput
                  value={formData.location}
                  onChange={(val) => setFormData({ ...formData, location: val })}
                  placeholder="Search golf courses..."
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label className="font-body text-bundle-green">Description</Label>
                <Textarea
                  data-testid="event-description-input"
                  placeholder="Tell members about the event, format, rules, etc."
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                />
              </div>

              {/* Flyer Image Upload */}
              <div className="space-y-2">
                <Label className="font-body text-bundle-green flex items-center gap-2">
                  <ImagePlus className="w-4 h-4" />
                  Event Flyer / Image (optional)
                </Label>
                <input
                  ref={flyerInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFlyerSelect}
                  className="hidden"
                  data-testid="flyer-file-input"
                />
                {flyerPreview ? (
                  <div className="relative inline-block">
                    <img
                      src={flyerPreview}
                      alt="Flyer preview"
                      data-testid="flyer-preview"
                      className="w-full max-w-sm rounded-xl border border-bundle-cream-dark object-cover"
                      style={{ maxHeight: 300 }}
                    />
                    <button
                      type="button"
                      onClick={removeFlyerImage}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors shadow-md"
                      data-testid="remove-flyer-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => flyerInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-bundle-gold hover:bg-bundle-gold/5 transition-all"
                    data-testid="flyer-dropzone"
                  >
                    <ImagePlus className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="font-body text-sm text-gray-500">Tap to upload a flyer or event image</p>
                    <p className="font-body text-xs text-gray-400 mt-1">JPG, PNG, GIF, WebP — Max 10MB</p>
                  </div>
                )}
              </div>

              {/* Max Participants & Entry Fee */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-body text-bundle-green flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Max Participants (optional)
                  </Label>
                  <Input
                    type="number"
                    data-testid="event-max-input"
                    placeholder="e.g., 16"
                    min="2"
                    max="100"
                    value={formData.max_participants}
                    onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                    className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-bundle-green flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Entry Fee (optional)
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    data-testid="event-fee-input"
                    placeholder="e.g., 25"
                    min="0"
                    value={formData.entry_fee}
                    onChange={(e) => setFormData({ ...formData, entry_fee: e.target.value })}
                    className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                  />
                </div>
              </div>

              {/* External Sign-up URL */}
              <div className="space-y-2">
                <Label className="font-body text-bundle-green flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Official Sign-up Link (optional)
                </Label>
                <Input
                  type="url"
                  data-testid="event-external-url-input"
                  placeholder="https://birdease.com/events/..."
                  value={formData.external_signup_url}
                  onChange={(e) => setFormData({ ...formData, external_signup_url: e.target.value })}
                  className="border-bundle-cream-dark focus:border-bundle-green focus:ring-bundle-green"
                />
                <p className="font-body text-xs text-gray-500">
                  Members will see a "Sign Up at Official Site" button alongside Bundle's "Count Me In".
                </p>
              </div>

              {/* Fundraiser fields (visible to fundraiser role + admins) */}
              {(isFundraiserUser || user?.is_admin) && (
                <div className="bg-bundle-gold/10 border border-bundle-gold/40 rounded-xl p-4 space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="event-is-fundraiser"
                      checked={formData.is_fundraiser}
                      onChange={(e) => setFormData({ ...formData, is_fundraiser: e.target.checked })}
                      className="w-4 h-4 accent-bundle-green"
                    />
                    <span className="font-body text-sm font-semibold text-bundle-green">Mark as fundraiser event</span>
                  </label>
                  {formData.is_fundraiser && (
                    <>
                      <div>
                        <Label className="font-body text-sm font-semibold text-bundle-green block mb-1">Charity info <span className="text-gray-400 font-normal">(optional)</span></Label>
                        <textarea
                          data-testid="event-charity-info"
                          rows={2}
                          maxLength={600}
                          placeholder="Who benefits from this event?"
                          value={formData.charity_info}
                          onChange={(e) => setFormData({ ...formData, charity_info: e.target.value })}
                          className="w-full rounded-md border border-bundle-cream-dark focus:border-bundle-green px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="font-body text-sm font-semibold text-bundle-green block mb-1">Sponsor info <span className="text-gray-400 font-normal">(optional)</span></Label>
                        <textarea
                          data-testid="event-sponsor-info"
                          rows={2}
                          maxLength={600}
                          placeholder="Sponsors, tiers, contact for sponsorship"
                          value={formData.sponsor_info}
                          onChange={(e) => setFormData({ ...formData, sponsor_info: e.target.value })}
                          className="w-full rounded-md border border-bundle-cream-dark focus:border-bundle-green px-3 py-2 text-sm"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ===== Sponsor Marketplace Opt-in ===== */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="event-seeking-sponsors"
                    checked={formData.seeking_sponsors}
                    onChange={(e) => setFormData({ ...formData, seeking_sponsors: e.target.checked })}
                    className="w-4 h-4 accent-blue-600"
                  />
                  <span className="font-body text-sm font-semibold text-blue-900">List this event on the Sponsor Marketplace</span>
                </label>
                {formData.seeking_sponsors && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold text-blue-900 block mb-1">Expected Attendance</Label>
                        <Input
                          data-testid="event-expected-attendance"
                          type="number"
                          value={formData.expected_attendance}
                          onChange={(e) => setFormData({ ...formData, expected_attendance: e.target.value })}
                          placeholder="e.g. 100"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-blue-900 block mb-1">Sponsor Contact Email</Label>
                        <Input
                          data-testid="event-sponsor-contact-email"
                          type="email"
                          value={formData.sponsor_contact_email}
                          onChange={(e) => setFormData({ ...formData, sponsor_contact_email: e.target.value })}
                          placeholder="Where sponsors reach you"
                          className="text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs font-semibold text-blue-900 block mb-2">Sponsorship Levels & Costs</Label>
                      <div className="space-y-2">
                        {formData.sponsorship_levels.map((lvl, idx) => (
                          <div key={idx} className="flex gap-2 items-start" data-testid={`sponsor-level-row-${idx}`}>
                            <Input
                              placeholder="Level (e.g. Hole Sponsor)"
                              value={lvl.name || ''}
                              onChange={(e) => {
                                const next = [...formData.sponsorship_levels];
                                next[idx] = { ...next[idx], name: e.target.value };
                                setFormData({ ...formData, sponsorship_levels: next });
                              }}
                              className="text-sm flex-1"
                            />
                            <Input
                              type="number"
                              placeholder="Cost ($)"
                              value={lvl.cost || ''}
                              onChange={(e) => {
                                const next = [...formData.sponsorship_levels];
                                next[idx] = { ...next[idx], cost: e.target.value };
                                setFormData({ ...formData, sponsorship_levels: next });
                              }}
                              className="text-sm w-28"
                            />
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, sponsorship_levels: formData.sponsorship_levels.filter((_, i) => i !== idx) })}
                              className="text-red-500 hover:text-red-700 text-xs px-2 py-1.5"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          data-testid="add-sponsor-level-btn"
                          onClick={() => setFormData({ ...formData, sponsorship_levels: [...formData.sponsorship_levels, { name: '', cost: '', description: '' }] })}
                          className="w-full py-2 px-3 rounded-lg border-2 border-dashed border-blue-300 text-blue-700 hover:bg-blue-100 text-xs font-bold"
                        >
                          + Add Sponsorship Level
                        </button>
                      </div>
                    </div>
                    <p className="text-[10px] text-blue-800 leading-relaxed">
                      Your event will appear on the public <a href="/sponsor-marketplace" target="_blank" rel="noopener" className="underline font-bold">Sponsor Marketplace</a> so approved sponsors can find and request to sponsor it.
                    </p>
                  </div>
                )}
              </div>


              {/* Submit Button */}
              <Button
                type="submit"
                data-testid="submit-event-btn"
                disabled={submitting}
                className="w-full btn-bundle py-6"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {uploadingFlyer ? 'Uploading Flyer...' : 'Submitting...'}
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    {isEditMode ? 'Save Changes' : (user.is_admin ? 'Create Event' : 'Submit for Approval')}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
